(() => {
  const mobileQuery = '(max-width: 749px)';
  const motionQuery = '(prefers-reduced-motion: reduce)';
  const feedRequests = new Map();

  function newestPage(input) {
    const url = new URL(input, window.location.origin);
    if (url.origin !== window.location.origin) throw new Error('Invalid catalogue origin');
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('section_id', 'ns-new-arrivals-feed');
    const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  async function fetchFeed(input) {
    const url = newestPage(input).href;
    if (!feedRequests.has(url)) {
      const request = fetch(url, { credentials: 'same-origin', cache: 'no-store' }).then(async response => {
        if (!response.ok) throw new Error('Catalogue unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const feed = doc.querySelector('[data-campaign-feed]');
        if (!feed || feed.dataset.sort !== 'created-descending') throw new Error('Catalogue sort unavailable');
        return feed;
      }).catch(error => { feedRequests.delete(url); throw error; });
      feedRequests.set(url, request);
    }
    return feedRequests.get(url);
  }

  if (!customElements.get('ns-campaign-carousel')) customElements.define('ns-campaign-carousel', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-campaign-track]');
      this.nav = this.querySelector('[data-campaign-nav]');
      this.previous = this.querySelector('[data-campaign-previous]');
      this.next = this.querySelector('[data-campaign-next]');
      this.position = this.querySelector('[data-campaign-position]');
      this.progress = this.querySelector('[data-campaign-progress]');
      if (!this.track) return;
      this.mobile = matchMedia(mobileQuery);
      this.motion = matchMedia(motionQuery);
      this.update = () => {
        const carousel = (this.mobile.matches ? this.dataset.mobileLayout : this.dataset.desktopLayout) === 'carousel';
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const left = Math.max(0, Math.min(maximum, Math.abs(this.track.scrollLeft)));
        const overflow = carousel && maximum > 2;
        if (this.nav) this.nav.hidden = !overflow || this.dataset.showControls === 'false';
        if (this.previous) this.previous.disabled = !overflow || left < 2;
        if (this.next) this.next.disabled = !overflow || left >= maximum - 2;
        const pageSize = Math.max(1, this.track.clientWidth);
        const bounds = this.track.getBoundingClientRect();
        const items = [...this.track.children];
        const visible = items.map((item, index) => ({ index, box: item.getBoundingClientRect() }))
          .filter(item => item.box.right > bounds.left + 2 && item.box.left < bounds.right - 2);
        if (this.position) {
          const first = visible.length ? visible[0].index + 1 : 1;
          const last = visible.length ? visible[visible.length - 1].index + 1 : items.length;
          this.position.textContent = first === last ? `${first} / ${items.length}` : `${first}–${last} / ${items.length}`;
        }
        if (this.progress) {
          const portion = Math.min(1, pageSize / Math.max(1, this.track.scrollWidth));
          this.progress.style.width = `${portion * 100}%`;
          this.progress.style.marginInlineStart = `${maximum ? (left / maximum) * (1 - portion) * 100 : 0}%`;
        }
        this.track.tabIndex = overflow ? 0 : -1;
      };
      const move = direction => {
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        this.track.scrollBy({ left: this.track.clientWidth * direction * (rtl ? -1 : 1), behavior: this.motion.matches ? 'auto' : 'smooth' });
      };
      this.previous?.addEventListener('click', () => move(-1), options);
      this.next?.addEventListener('click', () => move(1), options);
      this.track.addEventListener('scroll', this.update, { ...options, passive: true });
      this.addEventListener('campaign:updated', this.update, options);
      this.mobile.addEventListener('change', this.update, options);
      this.resize = new ResizeObserver(this.update);
      this.resize.observe(this.track);
      this.update();
    }
    disconnectedCallback() { this.controller?.abort(); this.resize?.disconnect(); }
  });

  if (!customElements.get('ns-campaign-media')) customElements.define('ns-campaign-media', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.videos = [...this.querySelectorAll('video')];
      this.toggle = this.querySelector('[data-campaign-video-toggle]');
      if (!this.videos.length || !this.toggle) return;
      this.mobile = matchMedia(mobileQuery);
      this.motion = matchMedia(motionQuery);
      this.userPaused = false;
      this.visible = false;
      this.sync = () => {
        const playing = this.active && !this.active.paused && !this.active.ended;
        this.toggle.textContent = playing ? this.dataset.pauseLabel : this.dataset.playLabel;
        this.toggle.setAttribute('aria-label', playing ? this.dataset.pauseDescription : this.dataset.playDescription);
      };
      this.play = () => {
        if (!this.active) return;
        this.active.muted = true;
        this.active.play().catch(() => this.sync());
      };
      this.chooseVideo = () => {
        this.videos.forEach(video => video.pause());
        this.active = this.mobile.matches
          ? this.querySelector('.ns-campaign-video--mobile') || this.videos[0]
          : this.querySelector('.ns-campaign-video--desktop') || this.videos[0];
        this.videos.forEach(video => { video.hidden = video !== this.active; });
        if (this.visible && !document.hidden && this.dataset.autoplay === 'true' && !this.motion.matches && !this.userPaused) this.play();
        this.sync();
      };
      for (const video of this.videos) {
        video.muted = true;
        video.addEventListener('playing', () => { video.classList.add('has-played'); this.sync(); }, options);
        video.addEventListener('pause', this.sync, options);
        video.addEventListener('ended', this.sync, options);
        video.addEventListener('error', this.sync, options);
      }
      this.toggle.addEventListener('click', () => {
        if (this.active.paused || this.active.ended) { this.userPaused = false; this.play(); }
        else { this.userPaused = true; this.active.pause(); }
      }, options);
      this.mobile.addEventListener('change', this.chooseVideo, options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.videos.forEach(video => video.pause());
        else this.chooseVideo();
      }, options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach(video => video.pause());
        else this.chooseVideo();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (!this.visible) this.videos.forEach(video => video.pause());
        else this.chooseVideo();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.chooseVideo();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.observer?.disconnect();
      this.videos?.forEach(video => video.pause());
    }
  });

  if (!customElements.get('ns-new-arrivals')) customElements.define('ns-new-arrivals', class extends HTMLElement {
    connectedCallback() {
      this.requestId = (this.requestId || 0) + 1;
      const requestId = this.requestId;
      const track = this.querySelector('[data-campaign-track]');
      const status = this.querySelector('[data-arrivals-status]');
      if (!track || !this.dataset.url) return;
      const load = async () => {
        const cards = [];
        const ids = new Set();
        const visited = new Set();
        const limit = Math.max(1, Math.min(24, Number(this.dataset.limit) || 8));
        let url;
        try {
          url = newestPage(this.dataset.url).href;
          while (url && cards.length < limit) {
            if (visited.has(url)) break;
            visited.add(url);
            const feed = await fetchFeed(url);
            if (!this.isConnected || requestId !== this.requestId) return;
            const source = feed.querySelector('[data-feed-cards]')?.content || feed;
            for (const original of source.querySelectorAll('.ns-campaign-product')) {
              const id = original.dataset.productId;
              if (ids.has(id)) continue;
              ids.add(id);
              if (this.dataset.hideSoldOut === 'true' && original.dataset.productAvailable !== 'true') continue;
              const card = original.cloneNode(true);
              if (this.dataset.showColors === 'false') card.querySelector('.ns-campaign-product__colors')?.remove();
              if (this.dataset.showWishlist === 'false') card.querySelector('[data-wishlist-toggle]')?.remove();
              cards.push(card);
              if (cards.length === limit) break;
            }
            url = feed.dataset.next ? newestPage(new URL(feed.dataset.next, url)).href : '';
          }
          if (!this.isConnected || requestId !== this.requestId) return;
          track.replaceChildren(...cards);
          if (!cards.length) {
            const message = document.createElement('p'); message.className = 'ns-campaign-editor-note'; message.textContent = this.dataset.emptyLabel;
            track.append(message);
          }
          if (status) status.textContent = this.dataset.loadedLabel;
          this.setAttribute('aria-busy', 'false');
          this.querySelector('ns-campaign-carousel')?.dispatchEvent(new CustomEvent('campaign:updated'));
        } catch {
          if (!this.isConnected || requestId !== this.requestId) return;
          track.replaceChildren();
          if (status) status.textContent = this.dataset.failedLabel || this.dataset.emptyLabel;
          this.setAttribute('aria-busy', 'false');
          this.querySelector('ns-campaign-carousel')?.dispatchEvent(new CustomEvent('campaign:updated'));
        }
      };
      load();
    }
    disconnectedCallback() { this.requestId = (this.requestId || 0) + 1; }
  });
})();
